import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { KeyValue, RepoRef } from '../types/environment';
import type { GoogleUser } from '../types/auth';
import RepoSelector from '../components/RepoSelector';
import Cookies from 'js-cookie';

interface OrgOption {
	org: string;
}

export default function NewEnvironmentPage() {
	const environmentID = 'default';
    const [orgs, setOrgs] = useState<OrgOption[]>([]);
    const [selectedRepos, setSelectedRepos] = useState<RepoRef[]>([]);
    const [name, setName] = useState('');
    const [context, setcontext] = useState('');
    // Default image to agent-wrapper:dev so that users have a sensible starting value
    const [image, setImage] = useState('plan42/agent');
    const [envVars, setEnvVars] = useState<KeyValue[]>([]);
    const [secrets, setSecrets] = useState<KeyValue[]>([]);
    const [bootstrap, setBootstrap] = useState('');
    const [endpoints, setEndpoints] = useState<string[]>([]);
    const navigate = useNavigate();
    const [errors, setErrors] = useState<string[]>([]);
    const [currentSecretIndex, setCurrentSecretIndex] = useState<null | number>(null);
    const CONTEXT_LIMIT = 25000;
    const BOOTSTRAP_LIMIT = 5000;
    const fieldStyle = { width: '100%', padding: '6px' } as const;
    const labelStyle = { width: '100%', display: 'flex', flexDirection: 'column' } as const;

	useEffect(() => {
		async function loadOrgs() {
			try {
				const resp = await fetch(`/api/github/orgs?environmentID=${encodeURIComponent(environmentID)}`);
                if (resp.ok) {
                    const data: OrgOption[] = await resp.json();
                    setOrgs(data);
                } else {
                    setOrgs([]);
                }
            } catch (err) {
                console.error('load orgs error', err);
            }
        }
		loadOrgs();
	}, [environmentID]);


    const hasSetDefaultName = useRef(false);
    useEffect(() => {
        if (!hasSetDefaultName.current && !name && selectedRepos.length > 0) {
            const first = selectedRepos[0];
            setName(`${first.Org}/${first.Repo}`);
            hasSetDefaultName.current = true;
        }
    }, [selectedRepos, name]);


    const addEnvVar = () => setEnvVars([...envVars, { Key: '', Value: '' }]);
    const updateEnvVar = (i: number, field: 'Key' | 'Value', value: string) => {
        const copy = [...envVars];
        copy[i][field] = value;
        setEnvVars(copy);
    };
    const removeEnvVar = (i: number) => setEnvVars(envVars.filter((_, idx) => idx !== i));

    const addSecret = () => setSecrets([...secrets, { Key: '', Value: '' }]);
    const updateSecret = (i: number, field: 'Key' | 'Value', value: string) => {
        const copy = [...secrets];
        copy[i][field] = value;
        setSecrets(copy);
    };
    const removeSecret = (i: number) => setSecrets(secrets.filter((_, idx) => idx !== i));

    const addEndpoint = () => setEndpoints([...endpoints, '']);
    const updateEndpoint = (i: number, value: string) => {
        const copy = [...endpoints];
        copy[i] = value;
        setEndpoints(copy);
    };
    const removeEndpoint = (i: number) => setEndpoints(endpoints.filter((_, idx) => idx !== i));

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
		try {
			const storedUser = Cookies.get('User');
			const email = storedUser ? (JSON.parse(storedUser) as GoogleUser).email : '';
			const repoPairs = selectedRepos.map((r) => ({ Org: r.Org, Repo: r.Repo }));
            const resp = await fetch(`/api/environments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-User-Email': email },
                body: JSON.stringify({
                    Repos: repoPairs,
                    Name: name,
                    Context: context,
                    Image: image,
                    EnvVars: envVars,
                    Secrets: secrets,
                    BootstrapScript: bootstrap,
                    AllowedEndpoints: endpoints,
                }),
            });
            if (resp.ok) {
                navigate('/environments');
            } else {
                const msg = await resp.text();
                setErrors((errs) => [...errs, msg]);
            }
        } catch (err) {
            console.error('CreateEnvironment error', err);
            setErrors((errs) => [...errs, 'CreateEnvironment error']);
        }
    }

    return (
		<div style={{ paddingTop: '60px', maxWidth: '800px', margin: '0 auto' }}>
            {errors.map((err, idx) => (
                <div key={idx} style={{ background: '#fdd', padding: '8px', marginBottom: '8px' }}>
                    {err}
                </div>
            ))}
            <h1>Create Environment</h1>
            <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
					<RepoSelector environmentID={environmentID} orgs={orgs} selectedRepos={selectedRepos} setSelectedRepos={setSelectedRepos} />
                    <label style={labelStyle}>
                        Name
                        <input value={name} onChange={(e) => setName(e.target.value)} style={fieldStyle} />
                    </label>
                    <label style={labelStyle}>
                        Context (optional AI instructions about this environment)
                        <textarea
                            value={context}
                            onChange={(e) => setcontext(e.target.value)}
                            style={{ ...fieldStyle, minHeight: '80px' }}
                            maxLength={CONTEXT_LIMIT}
                        />
                        <div style={{ textAlign: 'right', fontSize: '0.8em', color: '#555' }}>
                            {context.length}/{CONTEXT_LIMIT}
                        </div>
                    </label>
                    <label style={labelStyle}>
                        Image
                        <input value={image} onChange={(e) => setImage(e.target.value)} style={fieldStyle} />
                    </label>
                    <div>
                        <div>Environment variables</div>
                        <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={addEnvVar}
                            style={{ marginTop: '4px' }}
                        >
                            Add
                        </button>
                        {envVars.map((v, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                <input
                                    value={v.Key}
                                    onChange={(e) => updateEnvVar(i, 'Key', e.target.value)}
                                    placeholder="Key"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <input
                                    value={v.Value}
                                    onChange={(e) => updateEnvVar(i, 'Value', e.target.value)}
                                    placeholder="Value"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <button
                                    type="button"
                                    className="btn-small btn-secondary"
                                    onClick={() => removeEnvVar(i)}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                    <div>
                        <div>Secrets</div>
                        <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={addSecret}
                            style={{ marginTop: '4px' }}
                        >
                            Add
                        </button>
                        {secrets.map((s, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                <input
                                    value={s.Key}
                                    onChange={(e) => updateSecret(i, 'Key', e.target.value)}
                                    placeholder="Key"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <input
                                    type="password"
                                    value={currentSecretIndex == i ? s.Value : '*'.repeat(s.Value.length)}
                                    onChange={(e) => {
                                        setCurrentSecretIndex(i);
                                        updateSecret(i, 'Value', e.target.value);
                                    }}
                                    onBlur={(_) => setCurrentSecretIndex(null)}
                                    placeholder="Value"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <button
                                    type="button"
                                    className="btn-small btn-secondary"
                                    onClick={() => removeSecret(i)}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                    <label style={labelStyle}>
                        Bootstrap script
                        <textarea
                            value={bootstrap}
                            onChange={(e) => setBootstrap(e.target.value)}
                            style={{ ...fieldStyle, minHeight: '80px' }}
                            maxLength={BOOTSTRAP_LIMIT}
                        />
                        <div style={{ textAlign: 'right', fontSize: '0.8em', color: '#555' }}>
                            {bootstrap.length}/{BOOTSTRAP_LIMIT}
                        </div>
                    </label>
                    <div>
                        <div>Allowed endpoints</div>
                        <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={addEndpoint}
                            style={{ marginTop: '4px' }}
                        >
                            Add
                        </button>
                        {endpoints.map((ep, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                <input
                                    value={ep}
                                    onChange={(e) => updateEndpoint(i, e.target.value)}
                                    placeholder="Endpoint"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <button
                                    type="button"
                                    className="btn-small btn-secondary"
                                    onClick={() => removeEndpoint(i)}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <button type="submit" style={{ padding: '6px 12px' }}>
                            Create Environment
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
